import {DispatchCarryover} from "@ots-frontend/ots-data";
import {IconCalendarRepeat} from "@tabler/icons-react";
import {AdminPanel} from "../../components/AdminPanel";
import {DescriptionItem, DescriptionList} from "../../components/DescriptionList";
import {OtsDateTime} from "../../components/OtsDateTime";
import {OtsTimeline} from "../../components/OtsTimeline";

export type DispatchCarryoverPanelProps = {
  carryoverCount?: number;
  originalScheduleDttm?: number;
  lastCarryoverDttm?: number;
  scheduleDttm?: number;
  history?: DispatchCarryover[];
  historyLoading?: boolean;
  density?: "compact" | "default";
};

/**
 * Explains a dispatch the carryover job moved to later days because its driver was off shift.
 * Renders nothing for a dispatch that never carried over.
 */
export const DispatchCarryoverPanel = ({
  carryoverCount,
  originalScheduleDttm,
  lastCarryoverDttm,
  scheduleDttm,
  history,
  historyLoading,
  density = "default",
}: DispatchCarryoverPanelProps) => {
  if (!carryoverCount || carryoverCount < 1) return null;

  const times = carryoverCount === 1 ? "once" : `${carryoverCount} times`;

  return (
    <AdminPanel
      variant="dense"
      icon={<IconCalendarRepeat/>}
      title={`Carried over ${times}`}
      className="border-[var(--ots-warning-outline)]"
      bodyClassName={["@container", density === "compact" ? "p-3" : "p-4"].join(" ")}>
      <p className="text-sm text-[var(--ots-text-muted)]">
        The dispatch was still open while its driver was off shift, so it moved to the next day at the same time.
      </p>
      <DescriptionList responsive="container" compact={density === "compact"} className={density === "compact" ? "mt-3" : "mt-4"}>
        <DescriptionItem label="Originally scheduled"><OtsDateTime value={originalScheduleDttm}/></DescriptionItem>
        <DescriptionItem label="Now scheduled"><OtsDateTime value={scheduleDttm}/></DescriptionItem>
        <DescriptionItem label="Last carried over"><OtsDateTime value={lastCarryoverDttm}/></DescriptionItem>
      </DescriptionList>
      <div className={density === "compact" ? "mt-3" : "mt-4"}>
        {historyLoading
          ? <p role="status" className="text-xs text-[var(--ots-text-muted)]">Loading carryover history…</p>
          : <OtsTimeline
            ariaLabel="Carryover history"
            density="compact"
            emptyState="No carryover history is available."
            items={[...(history ?? [])]
              .sort((left, right) => right.carryoverNumber - left.carryoverNumber)
              .map((carryover, index) => ({
                id: carryover.id,
                current: index === 0,
                tone: "warning",
                label: <span>
                  <OtsDateTime value={carryover.fromScheduleDttm}/> → <OtsDateTime value={carryover.toScheduleDttm}/>
                </span>,
                description: <span>
                  Carryover {carryover.carryoverNumber}
                  {carryover.personName ? ` · ${carryover.personName} off shift` : " · unassigned"}
                  {" · moved "}<OtsDateTime value={carryover.createDttm}/>
                </span>,
              }))}/>}
      </div>
    </AdminPanel>
  );
};
